import {useEffect, useState} from 'react'
import {Center} from '@mantine/core'
import {Col, Modal, Row} from 'react-bootstrap'
import {changeTextCapital} from '../../../_metronic/helpers/AssetHelpers'
import InputErrorBox from '../../components/InputErrorBox'
import {useAuth} from '../../modules/auth'
import {
  cancelSubmit,
  deleteSalaryStructure,
  getSalaryStructure,
  getSalaryStructureById,
  handleInputChange,
  handleSubmit,
  validateForm,
} from './SalaryStructureFunctions'
import {Toolbar1} from '../../../_metronic/layout/components/toolbar/Toolbar1'
import {getUsers} from '../../../api/AdminAPI'
import {EDIT_ROW_COLORS} from '../../../utils/contants'

interface User {
  id: string
  firstName: string
  lastName: string
}

const SalaryStructure = () => {
  const [salarystructure, setSalaryStructure] = useState([
    {
      salaryStructureName: '',
      basic: '',
      HRA: '',
      DA: '',
      education: '',
      LTA: '',
      specialAllowance: '',
      variable: '',
      telephoneAndInternetReimbursement: '',
      carAndDriverReimbursement: '',
      mealCoupon: '',
      booksAndPeriodicals: '',
      PF: '',
      PT: '',
      ESIC: '',
      LWF: '',
    },
  ])
  const [editId, setEditId] = useState(null)
  const [inputError, setInputError] = useState<any>({})
  const [APIStatus, setAPIStatus] = useState({
    loading: false,
    error: null,
    submitLoading: false,
  })
  const {currentUser} = useAuth()
  const isAdminUser = currentUser?.isAdmin || false
  const initialState = {
    salaryStructureName: '',
    basic: false,
    HRA: false,
    DA: false,
    specialAllowance: false,
    PF: false,
    PT: false,
    education: false,
    LTA: false,
    variable: false,
    carAndDriverReimbursement: false,
    mealCoupon: false,
    booksAndPeriodicals: false,
    ESIC: false,
    LWF: false,
    telephoneAndInternetReimbursement: false,
  }
  const [inputField, setInputField] = useState(initialState)
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [users, setUsers] = useState<User[]>([])

  useEffect(() => {
    let mounted = true
    if (mounted) {
      getSalaryStructure(setSalaryStructure, setAPIStatus)
      setIsAddUserModalOpen(false)
      setIsEditModalOpen(false)
      getUsers()
        .then((userList: any) => {
          setUsers(userList.data)
        })
        .catch((error) => {
          console.error('Error fetching users:', error)
        })
    }
    return () => {
      mounted = false
    }
  }, [])

  const handleClose = () => {
    setIsAddUserModalOpen(false)
  }

  if (APIStatus.loading) {
    return <div>Loading...</div>
  }

  if (APIStatus.error) {
    return <div>Error: {APIStatus.error}</div>
  }

  const handleSelectAll = () => {
    const updatedField = {...inputField}
    for (const key in updatedField) {
      if (key !== 'salaryStructureName' && typeof updatedField[key] === 'boolean') {
        updatedField[key] = !updatedField[key]
      }
    }
    setInputField(updatedField)
  }

  return (
    <>
      <Toolbar1 />

      <Modal
        show={isAddUserModalOpen}
        onClose={() => setIsAddUserModalOpen(false)}
        animation={false}
        backdrop='static'
        onRequestClose={handleClose}
        centered
        size='lg'
      >
        <div className='modal-header d-flex justify-content-between align-items-center py-2'>
          <h2 className='fw-bolder m-0'>Salary Structure</h2>
          <button
            className='btn btn-sm btn-icon btn-active-icon-danger'
            data-kt-users-modal-action='close'
            onClick={() => {
              cancelSubmit(setInputField, initialState, setEditId, setInputError)
              handleClose()
            }}
          >
            <i className='fa-solid fa-xmark fs-1' />
          </button>
        </div>
        <Row className='m-3'>
          <Col md={12}>
            <legend className='required mb-4'>Salary Structure Name</legend>
            <input
              placeholder='Salary Structure Name'
              className='form-control'
              type='text'
              name='salaryStructureName'
              autoComplete='off'
              value={inputField.salaryStructureName}
              onChange={(event) => handleInputChange(event, setInputField, inputField)}
            />
            {<InputErrorBox Message={inputError.salaryStructureName} />}
          </Col>
        </Row>
        <div className='modal-body'>
          <div className='mb-4'>
            <div className='fv-row mb-4'>
              <fieldset>
                <legend className='mb-4'>Salary Structure Component</legend>
                <div className='form-check mb-4 me-3'>
                  <input
                    type='checkbox'
                    id='selectAll'
                    checked={Object.values(inputField).every(
                      (value) => typeof value === 'boolean' && value
                    )}
                    onChange={handleSelectAll}
                    className='form-check-input'
                  />
                  <label htmlFor='selectAll' className='form-check-label ms-2'>
                    Select All
                  </label>
                </div>
                <Row className='mb-3'>
                  {Object.entries(inputField)
                    .filter(([key, value]) => !['salaryStructureName'].includes(key))
                    .map(([key, value]) => (
                      <Col key={key} xs={6} sm={6} md={4} className='mb-4 text-break'>
                        <div className='form-check'>
                          <input
                            type='checkbox'
                            id={key}
                            name={key}
                            checked={inputField[key]}
                            onChange={(e) => handleInputChange(e, setInputField, inputField)}
                            className='form-check-input'
                          />
                          <label htmlFor={key} className='form-check-label ms-2'>
                            {['HRA', 'DA', 'basic', 'specialAllowance', 'PF', 'PT'].includes(
                              key
                            ) ? (
                              <>
                                {['HRA', 'DA', 'PF', 'PT', 'LTA', 'ESIC', 'LWF'].includes(key)
                                  ? key
                                  : key
                                      .replace(/([A-Z])/g, ' $1')
                                      .replace(/^./, (str) => str.toUpperCase())}
                                <span className='text-danger ms-1 fs-4 fw-bold'>*</span>
                              </>
                            ) : ['HRA', 'DA', 'PF', 'PT', 'LTA', 'ESIC', 'LWF'].includes(key) ? (
                              key
                            ) : (
                              key
                                .replace(/([A-Z])/g, ' $1')
                                .replace(/^./, (str) => str.toUpperCase())
                            )}
                          </label>
                        </div>
                      </Col>
                    ))}
                </Row>
                <InputErrorBox Message={inputError.basic} />
              </fieldset>
            </div>
          </div>
        </div>
        <div className='modal-footer py-2'>
          <button
            onClick={() => {
              cancelSubmit(setInputField, initialState, setEditId, setInputError)
              setInputError('')
            }}
            type='button'
            className='btn btn-secondary btn-sm'
            style={{minWidth: '80px'}}
          >
            Clear
          </button>
          <button
            type='submit'
            className='btn btn-primary btn-sm ms-2'
            onClick={() => {
              handleSubmit(
                inputField,
                getSalaryStructure,
                setSalaryStructure,
                setEditId,
                initialState,
                setInputField,
                editId,
                cancelSubmit,
                setAPIStatus,
                setInputError,
                validateForm,
                setAPIStatus,
                setIsAddUserModalOpen,
                setIsEditModalOpen
              )
            }}
          >
            {editId !== null ? 'Update' : 'Save'}
          </button>
        </div>
      </Modal>

      {/* //end of the form */}

      <div className='row g-5 g-xl-8'>
        <div className='col-xl-12'>
          {/* begin:: card */}
          <div className='card card-xl-stretch mb-xl-8'>
            {/* begin:: card header */}
            <div className='card-header'>
              <div className='card-title'>Salary Structures</div>
              <div
                className='d-flex mt-4 justify-content-end'
                data-kt-user-table-toolbar='base'
                style={{height: '3rem'}}
              >
                {/* Begin::Add User button */}
                <button
                  type='button'
                  className='btn btn-sm btn-primary'
                  onClick={() => {
                    setIsAddUserModalOpen(true)
                  }}
                >
                  Add Salary Structure
                </button>
                {/* end::Add user button */}
              </div>
            </div>
            {/* begin:: card body */}
            <div className='card-body'>
              {APIStatus.submitLoading ? (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Please wait...
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              ) : APIStatus.error != null ? (
                <div className='mb-lg-15 alert alert-danger'>
                  <div className='alert-text font-weight-bold'>{APIStatus.error}</div>
                </div>
              ) : (
                <div className='table-responsive'>
                  <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4 table-bordered'>
                    <thead>
                      <tr className='fw-bold text-muted'>
                        <th className='pl-1 text-center text-muted' style={{minWidth: '80px'}}>
                          {changeTextCapital('Sr.No')}
                        </th>
                        <th className='pl-1 flex-start text-muted' style={{minWidth: '150px'}}>
                          {changeTextCapital('Salary Structure')}
                        </th>
                        <th className='pl-1 text-center text-muted' style={{minWidth: '100px'}}>
                          {changeTextCapital('Actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className='text-gray-800 fw-semibold text-left'>
                      {salarystructure?.length > 0 ? (
                        salarystructure?.map((obj: any, index: number) => (
                          <tr
                            className={`${editId === obj?.id ? `${EDIT_ROW_COLORS} ` : ''}`}
                            key={obj?.id ?? '-'}
                          >
                            <td className='text-center'>{index + 1}</td>
                            <td className='flex-start'>{obj?.salaryStructureName ?? '-'}</td>
                            <td>
                              <div className='d-flex justify-content-center flex-shrink-0'>
                                <button
                                  className='btn btn-icon btn-bg-light btn-active-color-info btn-sm me-1'
                                  onClick={() => {
                                    document.getElementById('salary')?.focus()
                                    getSalaryStructureById(
                                      obj.id,
                                      inputField,
                                      setInputField,
                                      setEditId,
                                      setInputError
                                    )
                                    setIsEditModalOpen(true)
                                    setIsAddUserModalOpen(true)
                                  }}
                                >
                                  <i className='far fa-edit'></i>
                                </button>
                                <button
                                  className='btn btn-icon btn-bg-light btn-active-color-danger btn-sm me-1'
                                  onClick={() =>
                                    deleteSalaryStructure(
                                      obj.id,
                                      getSalaryStructure,
                                      setSalaryStructure,
                                      setAPIStatus,
                                      cancelSubmit,
                                      setInputField,
                                      initialState,
                                      setEditId,
                                      setInputError
                                    )
                                  }
                                >
                                  <i className='fa fa-trash'></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={3}>
                            <Center>No Record Available.</Center>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default SalaryStructure
